import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const FMT_PESOS = '"$"#,##0';
const FMT_NUM = '#,##0';
const FMT_DEC = '#,##0.0';
const FMT_PCT = '0.0%';

const hoja = (wb, nombre, titulo, columnas) => {
  const ws = wb.addWorksheet(nombre, { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  ws.addRow([titulo]).font = { bold: true, size: 13 };
  ws.addRow([]);
  const fila = ws.addRow(columnas.map((c) => c[0]));
  fila.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  fila.eachCell((c) => {
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F3460' } };
    c.alignment = { vertical: 'middle', wrapText: true };
  });
  fila.height = 30;
  columnas.forEach(([, ancho, fmt], i) => {
    ws.getColumn(i + 1).width = ancho;
    if (fmt) ws.getColumn(i + 1).numFmt = fmt;
  });
  return ws;
};

/** Excel del análisis de nómina: hojas por nómina, por planta y planta × nómina. */
export const exportarAnalisisNomina = async (d) => {
  if (!d) return;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'ERP Proflux';
  const titulo = `Análisis de nómina · ${d.desde} a ${d.hasta}`;
  const t = d.totales;

  const wn = hoja(wb, 'Por nómina', titulo, [
    ['Nómina', 30],
    ['Estado', 11],
    ['Desde', 11],
    ['Hasta', 11],
    ['Personas', 9, FMT_NUM],
    ['Horas trabajadas', 11, FMT_NUM],
    ['$ Básico', 14, FMT_PESOS],
    ['$ Auxilio transporte', 13, FMT_PESOS],
    ['$ Recargos', 13, FMT_PESOS],
    ['$ Extras', 13, FMT_PESOS],
    ['$ Devengado', 15, FMT_PESOS],
    ['$ Neto', 15, FMT_PESOS],
    ['$ Aportes empleador', 14, FMT_PESOS],
    ['$ Prestaciones', 14, FMT_PESOS],
    ['$ Costo total empresa', 15, FMT_PESOS],
    ['Kilos recibidos', 12, FMT_NUM],
    ['Devengado por kg', 11, FMT_PESOS],
    ['Devengado por hora', 11, FMT_PESOS],
    ['Kg por hora', 9, FMT_DEC],
    ['% recargos + extras', 10, FMT_PCT],
    ['Costo empresa por kg', 11, FMT_PESOS],
  ]);
  const filaN = (p, nombre) => [
    nombre ?? p.nombre,
    nombre ? '' : p.estado,
    nombre ? '' : p.desde,
    nombre ? '' : p.hasta,
    nombre ? null : p.personas,
    p.horas,
    p.basico,
    p.auxilio,
    p.recargos,
    p.extras,
    p.devengado,
    p.neto,
    p.aportes,
    p.prestaciones,
    p.costoEmpresa,
    p.kilos,
    p.costoKg,
    p.costoHora,
    p.kgHora,
    p.pctRecargosExtras,
    p.costoEmpresaKg,
  ];
  d.periodos.forEach((p) => wn.addRow(filaN(p)));
  wn.addRow(filaN(t, 'TOTAL')).font = { bold: true };

  const wc = hoja(wb, 'Por planta', titulo, [
    ['Planta', 28],
    ['Nóminas', 9, FMT_NUM],
    ['Personas', 9, FMT_NUM],
    ['Horas trabajadas', 11, FMT_NUM],
    ['$ Básico', 14, FMT_PESOS],
    ['$ Auxilio transporte', 13, FMT_PESOS],
    ['$ Recargos', 13, FMT_PESOS],
    ['$ Extras', 13, FMT_PESOS],
    ['$ Devengado', 15, FMT_PESOS],
    ['% del devengado', 10, FMT_PCT],
    ['Kilos recibidos', 12, FMT_NUM],
    ['% de los kilos', 10, FMT_PCT],
    ['Devengado por kg', 11, FMT_PESOS],
    ['Devengado por hora', 11, FMT_PESOS],
    ['Kg por hora', 9, FMT_DEC],
    ['% recargos + extras', 10, FMT_PCT],
    ['$ Aportes empleador', 14, FMT_PESOS],
    ['$ Prestaciones', 14, FMT_PESOS],
    ['$ Costo total empresa', 15, FMT_PESOS],
    ['Costo empresa por kg', 11, FMT_PESOS],
  ]);
  for (const c of d.centros) {
    wc.addRow([
      c.nombre,
      c.nominas,
      c.personas,
      c.horas,
      c.basico,
      c.auxilio,
      c.recargos,
      c.extras,
      c.devengado,
      c.participacionCosto,
      c.kilos,
      c.participacionKilos,
      c.costoKg,
      c.costoHora,
      c.kgHora,
      c.pctRecargosExtras,
      c.aportes,
      c.prestaciones,
      c.costoEmpresa,
      c.costoEmpresaKg,
    ]);
  }
  wc.addRow(['TOTAL', t.nominas, null, t.horas, t.basico, t.auxilio, t.recargos, t.extras, t.devengado, 1, t.kilos, 1, t.costoKg, t.costoHora, t.kgHora, t.pctRecargosExtras, t.aportes, t.prestaciones, t.costoEmpresa, t.costoEmpresaKg]).font = { bold: true };

  // Planta × nómina: costo y kilos de cada planta en cada nómina.
  const nombresNomina = Object.fromEntries(d.periodos.map((p) => [p.id, p.nombre]));
  const wx = hoja(wb, 'Planta por nómina', titulo, [
    ['Nómina', 30],
    ['Planta', 28],
    ['$ Devengado', 15, FMT_PESOS],
    ['Horas trabajadas', 11, FMT_NUM],
    ['Kilos recibidos', 12, FMT_NUM],
    ['Devengado por kg', 11, FMT_PESOS],
    ['$ Costo total empresa', 15, FMT_PESOS],
    ['Costo empresa por kg', 11, FMT_PESOS],
  ]);
  for (const x of d.centroPeriodo) {
    wx.addRow([
      nombresNomina[x.nominaId],
      x.centro,
      x.devengado,
      x.horas,
      x.kilos,
      x.kilos > 0 ? x.devengado / x.kilos : null,
      x.costoEmpresa,
      x.kilos > 0 ? x.costoEmpresa / x.kilos : null,
    ]);
  }

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `Analisis_nomina_${d.desde}_a_${d.hasta}.xlsx`,
  );
};
